'use client'

import { ArrowUpRightIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import {
  CATEGORIES,
  Category,
  CATEGORY_COLORS,
  CATEGORY_GROUPS,
} from '@/blocks/FlchLicenseCategories/categories'
import { PageInfo } from '@/src/@types/models'
import Container from '@/src/components/layout/Container'

export default function Component({ pageInfo }: { pageInfo: PageInfo }) {
  return (
    <div
      className={clsx({
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container>
        <h2 className="text-4xl font-black md:text-7xl">Kategorien</h2>
        <p className="mt-6 text-sm text-gray-600">
          Navigiere zu deiner Wunschkategorie
        </p>
        <div className="mt-3 flex flex-col gap-3 md:flex-row md:gap-8">
          {CATEGORY_GROUPS.map((group, index) => (
            <div
              key={index}
              className="grid grid-cols-3 gap-x-2 gap-y-3 md:w-102 md:gap-x-3 md:gap-y-4"
            >
              {group.map((category) => (
                <CategoryLink key={category.id} category={category} />
              ))}
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-lg bg-gray-50 p-5 md:w-102 md:pr-8">
          <p className="text-sm font-bold">Deine Kategorie ist nicht dabei?</p>
          <p className="mt-1 text-sm text-gray-800">
            Auf der Seite des asa findest du alle Kategorien mit noch weiteren
            detaillierten Informationen.
          </p>
          <Link
            href="https://fuehrerausweise.ch/ausweiskategorien/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-blue-700"
          >
            Zur asa Webseite
            <ArrowUpRightIcon size={20} weight="bold" />
          </Link>
        </div>
      </Container>
      <Container className="mt-12 md:mt-24">
        <div className="-mx-5 flex flex-col gap-1 bg-gray-150 md:mx-0 md:max-w-208 md:gap-0 md:divide-y md:divide-gray-150 md:bg-transparent">
          {CATEGORIES.map((category) => (
            <CategoryDetails key={category.id} category={category} />
          ))}
        </div>
      </Container>
    </div>
  )
}

function CategoryLink({ category }: { category: Category }) {
  const { icon: Icon, color, code, title } = category
  const colors = CATEGORY_COLORS[color]

  return (
    <a
      href={`#${category.id}`}
      aria-label={title}
      className={clsx(
        'flex flex-col gap-1 rounded-lg border bg-linear-to-b pt-3 pr-4 pb-2 pl-4 md:gap-3',
        colors.box
      )}
    >
      <Icon size={24} weight="bold" className={colors.icon} />
      <span className="text-right text-xl font-black md:text-4xl">{code}</span>
    </a>
  )
}

function CategoryDetails({ category }: { category: Category }) {
  const { icon: Icon, color } = category
  const colors = CATEGORY_COLORS[color]

  return (
    <article
      id={category.id}
      className="scroll-mt-20 bg-white px-5 py-8 md:bg-transparent md:px-0 md:py-12 md:first:pt-0 md:last:pb-0"
    >
      <div className="flex gap-5 md:gap-6">
        <div
          className={clsx(
            'flex size-14 shrink-0 items-center justify-center rounded-lg border bg-linear-to-b',
            colors.box
          )}
        >
          <Icon size={32} weight="bold" className={colors.icon} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-5 md:gap-6">
          <div>
            <h3 className="text-xl font-black md:text-4xl">{category.title}</h3>
            <p className="mt-1 text-sm text-gray-600">{category.subtitle}</p>
          </div>
          <p className="-ml-19 text-gray-800 md:ml-0">{category.description}</p>
          <div className="-ml-19 flex flex-col gap-5 md:ml-0 md:gap-4">
            <Fact label="Mindestalter" value={category.minAge} />
            <Fact label="Voraussetzungen" value={category.requirements} />
          </div>
        </div>
      </div>
    </article>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:gap-6">
      <p className="font-bold text-gray-800 md:w-37.5 md:shrink-0">{label}</p>
      <p className="text-gray-800">{value}</p>
    </div>
  )
}
